import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type Period = {
  from: string;
  to: string;
  /** Rótulo do atalho escolhido, ou `personalizado`. */
  preset: string;
};

type PeriodState = {
  period: Period;
  setPeriod: (period: Period) => void;
  applyPreset: (id: string) => void;
  /** `?from=...&to=...`, pronto para concatenar na URL da API. */
  query: string;
};

const STORAGE_KEY = 'sf.period';

function iso(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * Atalhos de período.
 *
 * `mes-atual` é o padrão porque é o recorte em que a maioria das perguntas
 * financeiras é feita — "como está este mês". Os demais existem para as
 * perguntas que sobram, e `personalizado` para o resto.
 */
export const PRESETS: Array<{ id: string; label: string; range: () => { from: string; to: string } }> = [
  {
    id: 'mes-atual',
    label: 'Este mês',
    range: () => {
      const now = new Date();
      return {
        from: iso(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: iso(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
      };
    },
  },
  {
    id: 'mes-anterior',
    label: 'Mês passado',
    range: () => {
      const now = new Date();
      return {
        from: iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        to: iso(new Date(now.getFullYear(), now.getMonth(), 0)),
      };
    },
  },
  {
    id: 'ultimos-3',
    label: 'Últimos 3 meses',
    range: () => {
      const now = new Date();
      return {
        from: iso(new Date(now.getFullYear(), now.getMonth() - 2, 1)),
        to: iso(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
      };
    },
  },
  {
    id: 'ultimos-12',
    label: 'Últimos 12 meses',
    range: () => {
      const now = new Date();
      return {
        from: iso(new Date(now.getFullYear(), now.getMonth() - 11, 1)),
        to: iso(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
      };
    },
  },
  {
    id: 'ano-atual',
    label: 'Este ano',
    range: () => {
      const now = new Date();
      return { from: iso(new Date(now.getFullYear(), 0, 1)), to: iso(new Date(now.getFullYear(), 11, 31)) };
    },
  },
  {
    id: 'ano-anterior',
    label: 'Ano passado',
    range: () => {
      const year = new Date().getFullYear() - 1;
      return { from: iso(new Date(year, 0, 1)), to: iso(new Date(year, 11, 31)) };
    },
  },
];

function defaultPeriod(): Period {
  const stored = localStorage.getItem(STORAGE_KEY);

  if (stored) {
    try {
      const parsed = JSON.parse(stored) as Period;

      /**
       * Atalho guardado é RECALCULADO, não restaurado.
       *
       * Quem escolheu "Este mês" em julho e volta em agosto quer agosto, não
       * julho congelado. Só `personalizado` guarda as datas literais — nele o
       * usuário escolheu um intervalo específico, e mexer nele seria ignorar a
       * escolha.
       */
      if (parsed.preset && parsed.preset !== 'personalizado') {
        const preset = PRESETS.find((p) => p.id === parsed.preset);
        if (preset) return { ...preset.range(), preset: preset.id };
      }

      if (parsed.from && parsed.to) return parsed;
    } catch {
      // Valor corrompido no localStorage não pode impedir o app de subir.
    }
  }

  return { ...PRESETS[0]!.range(), preset: PRESETS[0]!.id };
}

const PeriodContext = createContext<PeriodState | null>(null);

/**
 * Período global — RF-DAS-03.
 *
 * ================================================================
 * POR QUE CONTEXTO E NÃO PROPRIEDADE, COMO O SELETOR DE ENTIDADES
 * ================================================================
 *
 * O período é consumido por componentes ANINHADOS — o extrato dentro de Contas,
 * o painel de saldo dentro do Acerto, o detalhe dentro de Dívidas. Passá-lo por
 * propriedade obrigaria a atravessar dois ou três níveis em cada tela só para
 * repassar o mesmo par de datas.
 *
 * `selectedIds` continua por propriedade porque já estava assim e é consumido
 * no topo de cada tela. Misturar os dois padrões tem um custo de coerência que
 * eu preferi pagar aqui em vez de reescrever quinze arquivos.
 */
export function PeriodProvider({ children }: { children: ReactNode }) {
  const [period, setPeriodState] = useState<Period>(defaultPeriod);

  const persist = useCallback((next: Period) => {
    setPeriodState(next);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Modo privado ou storage cheio: o período vale para a sessão e pronto.
    }
  }, []);

  const applyPreset = useCallback(
    (id: string) => {
      const preset = PRESETS.find((p) => p.id === id);
      if (preset) persist({ ...preset.range(), preset: preset.id });
    },
    [persist],
  );

  const value = useMemo<PeriodState>(
    () => ({
      period,
      setPeriod: persist,
      applyPreset,
      query: `from=${period.from}&to=${period.to}`,
    }),
    [period, persist, applyPreset],
  );

  return <PeriodContext.Provider value={value}>{children}</PeriodContext.Provider>;
}

export function usePeriod(): PeriodState {
  const context = useContext(PeriodContext);
  if (!context) throw new Error('usePeriod precisa estar dentro de <PeriodProvider>');
  return context;
}

/** Rótulo curto para a barra superior: "Ago/2026" ou "01/06 a 31/08". */
export function periodLabel(period: Period): string {
  const preset = PRESETS.find((p) => p.id === period.preset);
  if (preset) return preset.label;

  const short = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
  const sameYear = period.from.slice(0, 4) === period.to.slice(0, 4);

  return sameYear
    ? `${short(period.from)} a ${short(period.to)}/${period.to.slice(2, 4)}`
    : `${short(period.from)}/${period.from.slice(2, 4)} a ${short(period.to)}/${period.to.slice(2, 4)}`;
}
